import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import SMSMarketingPopup from '../sms-marketing-popup';

const springEasing = [0.22, 1, 0.36, 1];

const Contact = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSmsPopup, setShowSmsPopup] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    ['#features', 'Tjenester', '/landing-page'],
    ['/toll-calculator', 'Priser', null],
    ['/contact', 'Kontakt', null],
    ['/trust-and-security', 'Sikkerhet', null],
    ['/about-us', 'Om oss', null],
    ['/faq', 'FAQ', null],
  ];

  const handleNavClick = (href, fallback) => {
    setMobileOpen(false);
    if (href?.startsWith('#')) {
      navigate(fallback || '/landing-page');
    } else {
      navigate(href);
    }
  };

  const handleSmsOptIn = (phone) => {
    setSmsPhone(phone);
    setShowSmsPopup(true);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)', position: 'relative', overflow: 'hidden' }}>
      {/* Architectural background geometry */}
      <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '400px', height: '400px', border: '2px solid rgba(0,79,152,0.06)', borderRadius: '2px', transform: 'rotate(18deg)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '200px', right: '-40px', width: '200px', height: '200px', border: '1px solid rgba(255,87,34,0.07)', borderRadius: '2px', transform: 'rotate(12deg)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '80px', left: '-60px', width: '280px', height: '280px', border: '2px solid rgba(0,79,152,0.05)', borderRadius: '2px', transform: 'rotate(-14deg)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '40%', left: '50%', width: '1px', height: '200px', background: 'rgba(255,87,34,0.05)', transform: 'rotate(25deg)', pointerEvents: 'none' }} />
      {/* Public navigation header */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: '64px',
        background: 'rgba(10,12,16,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(224,228,232,0.06)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 32px',
      }}>
        <div style={{ maxWidth: '1440px', width: '100%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo */}
          <button
            onClick={() => navigate('/landing-page')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', padding: 0 }}
          >
            <div style={{ width: '28px', height: '28px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
            </div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 700, color: 'var(--color-foreground)' }}>Veiassistent</span>
          </button>

          {/* Nav links - hidden on mobile */}
          <div className="hidden-mobile" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {navLinks?.map(([href, label, fallback]) => {
              const isActive = !href?.startsWith('#') && location?.pathname === href;
              return (
                <button
                  key={href}
                  onClick={() => handleNavClick(href, fallback)}
                  style={{
                    background: 'none',
                    border: 'none',
                    borderBottom: isActive ? '2px solid #FF5722' : '2px solid transparent',
                    padding: '2px 0',
                    fontFamily: 'var(--font-heading)',
                    fontSize: '13px',
                    fontWeight: isActive ? 700 : 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: isActive ? '#FF5722' : 'var(--color-muted-foreground)',
                    cursor: 'pointer',
                    transition: 'color 150ms ease, border-color 150ms ease',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-foreground)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Auth buttons - hidden on mobile */}
          <div className="hidden-mobile" style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/login')}
              style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '8px 20px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}
            >
              Logg inn
            </button>
            <button
              onClick={() => navigate('/register')}
              style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '8px 20px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}
            >
              Registrer deg
            </button>
          </div>

          {/* Hamburger - mobile only */}
          <button
            className="show-mobile"
            onClick={() => setMobileOpen(p => !p)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '8px', zIndex: 110 }}
          >
            <div style={{ width: '24px', height: '20px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <motion.span animate={mobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3, ease: springEasing }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
              <motion.span animate={mobileOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }} transition={{ duration: 0.2 }} style={{ display: 'block', height: '2px', width: '100%', background: 'var(--color-foreground)', borderRadius: '2px' }} />
              <motion.span animate={mobileOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3, ease: springEasing }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
            </div>
          </button>
        </div>
      </nav>
      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div key="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 105, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }} />
            <motion.div key="panel" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ duration: 0.4, ease: springEasing }} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px', zIndex: 106, background: 'rgba(26,29,32,0.98)', backdropFilter: 'blur(20px)', borderLeft: '1px solid rgba(224,228,232,0.08)', display: 'flex', flexDirection: 'column', paddingTop: '88px', paddingBottom: '32px', paddingLeft: '32px', paddingRight: '32px' }}>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '32px' }}>
                {navLinks?.map(([href, label, fallback], i) => {
                  const isActive = !href?.startsWith('#') && location?.pathname === href;
                  return (<motion.button key={href} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.05, duration: 0.35, ease: springEasing }} onClick={() => handleNavClick(href, fallback)} style={{ background: isActive ? 'rgba(255,87,34,0.1)' : 'none', border: 'none', borderLeft: isActive ? '3px solid #FF5722' : '3px solid transparent', borderRadius: '0 4px 4px 0', padding: '12px 16px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: isActive ? 700 : 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: isActive ? '#FF5722' : 'var(--color-muted-foreground)', cursor: 'pointer', textAlign: 'left' }}>{label}</motion.button>);
                })}
              </nav>
              <div style={{ height: '1px', background: 'rgba(224,228,232,0.08)', marginBottom: '28px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button onClick={() => { setMobileOpen(false); navigate('/login'); }} style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}>Logg inn</button>
                <button onClick={() => { setMobileOpen(false); navigate('/register'); }} style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}>Registrer deg</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <main className="pt-[84px]" style={{ position: 'relative', zIndex: 1 }}>
        {/* Hero section */}
        <section style={{ padding: '60px 24px 0', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0', marginBottom: '8px' }}>
            <div style={{ width: '4px', height: '48px', background: '#FF5722', marginRight: '20px', marginTop: '4px', flexShrink: 0 }} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#FF5722' }}>Kontakt oss</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,87,34,0.3)', maxWidth: '60px' }} />
              </div>
              <h1 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(28px, 5vw, 34px)',
                fontWeight: 700,
                letterSpacing: '0.02em',
                color: 'var(--color-foreground)',
                lineHeight: 1.15,
                marginBottom: '16px',
              }}>
                La oss snakke
                <span style={{ display: 'block', color: 'rgba(224,228,232,0.4)', fontSize: '0.65em', fontWeight: 400, letterSpacing: '0.08em' }}>— vi er her for deg</span>
              </h1>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.7, maxWidth: '520px' }}>
                Har du spørsmål om bomavgifter, abonnement eller teknisk støtte? Fyll ut skjemaet eller bruk en av våre direkte kontaktkanaler.
              </p>
            </div>
          </div>

          {/* SMS promo banner */}
          <div
            onClick={() => setShowSmsPopup(true)}
            style={{
              marginTop: '32px',
              padding: '16px 24px',
              background: 'rgba(255,87,34,0.07)',
              border: '1px solid rgba(255,87,34,0.2)',
              borderLeft: '4px solid #FF5722',
              borderRadius: '0 2px 2px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              cursor: 'pointer',
              transition: 'background 200ms ease',
              maxWidth: '680px',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,87,34,0.12)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,87,34,0.07)'}
          >
            <span style={{ fontSize: '22px' }}>📱</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600, color: '#FF5722', marginBottom: '2px' }}>
                Abonner på SMS-varsler
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-muted-foreground)' }}>
                Få eksklusive tilbud og bomtakst-oppdateringer direkte på mobilen
              </p>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF5722" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </section>

        {/* Main content grid */}
        <section style={{ padding: '48px 24px 80px', maxWidth: '1200px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
              gap: '48px',
              alignItems: 'start',
            }} className="contact-grid">
            {/* Form panel */}
            <div style={{ position: 'relative' }}>
              {/* Offset background accent */}
              <div style={{
                position: 'absolute',
                top: '-8px', left: '-8px',
                right: '8px', bottom: '8px',
                background: 'rgba(255,87,34,0.03)',
                border: '1px solid rgba(255,87,34,0.07)',
                borderRadius: '2px',
                pointerEvents: 'none',
              }} />
              <div style={{
                position: 'relative',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(224,228,232,0.1)',
                borderTop: '3px solid rgba(255,87,34,0.6)',
                borderRadius: '2px',
                padding: '36px',
              }}>
                {/* Form header */}
                <div style={{ marginBottom: '32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <div style={{ width: '28px', height: '2px', background: '#FF5722' }} />
                    <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#FF5722' }}>Skriv til oss</span>
                  </div>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', fontWeight: 600, color: 'var(--color-foreground)', letterSpacing: '0.01em' }}>
                    Send en henvendelse
                  </h2>
                </div>
                <ContactForm onSmsOptIn={handleSmsOptIn} />
              </div>
            </div>

            {/* Info panel */}
            <div style={{ position: 'sticky', top: '100px' }}>
              <ContactInfo />

              {/* SMS CTA card */}
              <div
                onClick={() => setShowSmsPopup(true)}
                style={{
                  marginTop: '24px',
                  padding: '24px',
                  background: 'linear-gradient(135deg, rgba(255,87,34,0.1) 0%, rgba(0,79,152,0.08) 100%)',
                  border: '1px solid rgba(255,87,34,0.2)',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'border-color 200ms ease',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,87,34,0.4)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,87,34,0.2)'}
              >
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', border: '2px solid rgba(255,87,34,0.1)', borderRadius: '2px', transform: 'rotate(20deg)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '36px', height: '36px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                      <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                  </div>
                  <p style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: 600, color: 'var(--color-foreground)' }}>SMS-markedsføring</p>
                </div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-muted-foreground)', lineHeight: 1.6, marginBottom: '16px' }}>
                  Registrer deg for å motta eksklusive tilbud og oppdateringer via SMS.
                </p>
                <button
                  style={{ background: '#FF5722', border: 'none', borderRadius: '2px', padding: '10px 20px', fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}
                >
                  Registrer deg
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(224,228,232,0.06)', padding: '32px 32px 24px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '24px', height: '24px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
              </div>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 700, color: 'var(--color-foreground)' }}>Veiassistent</span>
            </div>
            <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>© 2026 Veiassistent AS. Alle rettigheter forbeholdt.</span>
          </div>
        </footer>
      </main>
      {showSmsPopup && (
        <SMSMarketingPopup
          initialPhone={smsPhone}
          onClose={() => setShowSmsPopup(false)}
        />
      )}
    </div>
  );
};

export default Contact;
